import type { Meal } from "@/packages/dashboard/api/get-meals";
import { Stack, Text } from "@mantine/core";

import { formatMealLine } from "../meal-summary-format";
import { computeItemTotals, formatMacroValue } from "./macro-aggregations";

const formatNutritionLine = (totals: { calories: number; proteins: number; carbs: number; fats: number }) => {
  return `${formatMacroValue(totals.calories)} kcal · ${formatMacroValue(totals.proteins)}g protein · ${formatMacroValue(totals.carbs)}g carbs · ${formatMacroValue(totals.fats)}g fat`;
};

/**
 * Returns a background color style that visualizes calorie contribution to daily target.
 * Uses a green→yellow→red gradient where:
 * - Green (0-10%): Low-impact items
 * - Yellow (10-20%): Moderate items
 * - Red (20-30%+): High-impact items
 */
const getCalorieIntensityStyle = (calories: number, dailyTarget: number | null | undefined) => {
  if (!dailyTarget || dailyTarget <= 0) {
    return {};
  }

  const percentage = (calories / dailyTarget) * 100;

  // Normalize to 0-1 scale where 30% of daily target = 1.0 (full red)
  const normalizedValue = Math.min(percentage / 30, 1);

  // Map to hue: 120 (green) → 60 (yellow) → 0 (red)
  const hue = 120 * (1 - normalizedValue);

  // Scale opacity: subtle for low values, more visible for high values
  const opacity = 0.12 + normalizedValue * 0.18; // 12% to 30%

  return {
    backgroundColor: `hsla(${hue}, 70%, 50%, ${opacity})`,
  };
};

const MealItemsTable = ({ meal, dailyCalorieTarget }: { meal: Meal; dailyCalorieTarget?: number | null }) => {
  if (meal.meal_items.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No meal items logged.
      </Text>
    );
  }

  return (
    <Stack gap={8}>
      {meal.meal_items.map((item) => {
        const food = item.food_item;
        const foodName = food?.name ?? "Unknown food";
        const summary = formatMealLine(item.quantity, food?.serving_unit, foodName);
        const totals = food ? computeItemTotals(item.quantity, food) : { calories: 0, proteins: 0, carbs: 0, fats: 0 };
        const nutritionLine = formatNutritionLine(totals);

        const intensityStyle = getCalorieIntensityStyle(totals.calories, dailyCalorieTarget);

        return (
          <Stack
            key={item.id}
            gap={2}
            style={{
              borderLeft: "1px solid #E5E7EB",
              paddingLeft: 12,
              borderRadius: 4,
              padding: "8px 12px",
              marginLeft: -4,
              transition: "background-color 0.15s ease",
              ...intensityStyle,
            }}
          >
            <Text size="sm" fw={600} style={{ color: "#111827" }}>
              {summary}
            </Text>
            <Text size="xs" style={{ color: "#4B5563" }}>
              {nutritionLine}
            </Text>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default MealItemsTable;
